(() => {
  const body = document.body;
  const qs = (sel, ctx = document) => ctx.querySelector(sel);
  const qsa = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  /* ══════════════════════════════════════
     UTILITIES
     ══════════════════════════════════════ */
  const formatMoney = (cents) => `R ${(cents / 100).toFixed(2)}`;

  const debounce = (fn, ms) => {
    let timer;
    return (...args) => { clearTimeout(timer); timer = setTimeout(() => fn(...args), ms); };
  };

  /* ══════════════════════════════════════
     DRAWERS — Shop, Mobile, Account, Cart
     ══════════════════════════════════════ */
  const drawerSetup = (openSel, closeSel, drawerSel, backdropSel, dir = 'right') => {
    const drawer = qs(drawerSel);
    const backdrop = qs(backdropSel);
    if (!drawer) return { open: () => {}, close: () => {} };

    const hideClass = dir === 'left' ? '-translate-x-full' : 'translate-x-full';
    const showClass = 'translate-x-0';

    const open = () => {
      drawer.classList.remove(hideClass);
      drawer.classList.add(showClass);
      if (backdrop) backdrop.classList.remove('hidden');
      body.classList.add('drawer-open');
    };
    const close = () => {
      drawer.classList.add(hideClass);
      drawer.classList.remove(showClass);
      if (backdrop) backdrop.classList.add('hidden');
      body.classList.remove('drawer-open');
    };

    document.addEventListener('click', (e) => {
      if (e.target.closest(openSel)) open();
      if (e.target.closest(closeSel) || e.target.closest(backdropSel)) close();
    });

    return { open, close };
  };

  const shopDrawer = drawerSetup('[data-shop-menu-open]', '[data-shop-menu-close]', '[data-shop-menu]', '[data-shop-menu-backdrop]', 'left');
  const mobileDrawer = drawerSetup('[data-mobile-menu-open]', '[data-mobile-menu-close]', '[data-mobile-menu]', '[data-mobile-menu-backdrop]', 'left');
  const accountDrawer = drawerSetup('[data-account-open]', '[data-account-close]', '[data-account-drawer]', '[data-account-backdrop]', 'right');

  /* Cart drawer — manual setup for extra open logic */
  const cartDrawer = qs('[data-cart-drawer]');
  const cartBackdrop = qs('[data-cart-backdrop]');
  const openCart = () => {
    if (!cartDrawer) return;
    cartDrawer.classList.remove('translate-x-full');
    cartDrawer.classList.add('translate-x-0');
    if (cartBackdrop) cartBackdrop.classList.remove('hidden');
    body.classList.add('drawer-open');
  };
  const closeCart = () => {
    if (!cartDrawer) return;
    cartDrawer.classList.add('translate-x-full');
    cartDrawer.classList.remove('translate-x-0');
    if (cartBackdrop) cartBackdrop.classList.add('hidden');
    body.classList.remove('drawer-open');
  };
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-cart-close]')) closeCart();
  });

  /* ══════════════════════════════════════
     SEARCH OVERLAY
     ══════════════════════════════════════ */
  const searchOverlay = qs('[data-search]');
  const searchPanel = qs('[data-search-panel]');
  const openSearch = () => {
    if (!searchOverlay) return;
    const header = qs('[data-header]') || qs('nav.sticky');
    if (header && searchPanel) searchPanel.style.top = header.getBoundingClientRect().bottom + 'px';
    searchOverlay.classList.remove('pointer-events-none', 'invisible');
    searchOverlay.classList.add('pointer-events-auto', 'visible');
    const closeBtn = searchOverlay.querySelector('[data-search-close]');
    if (closeBtn) { closeBtn.classList.add('opacity-100'); closeBtn.classList.remove('opacity-0'); }
    if (searchPanel) { searchPanel.classList.remove('-translate-y-full'); searchPanel.classList.add('translate-y-0'); }
    body.classList.add('drawer-open');
    const input = searchOverlay.querySelector('[data-search-input]');
    if (input) setTimeout(() => input.focus(), 100);
  };
  const closeSearch = () => {
    if (!searchOverlay) return;
    searchOverlay.classList.add('pointer-events-none', 'invisible');
    searchOverlay.classList.remove('pointer-events-auto', 'visible');
    const closeBtn = searchOverlay.querySelector('[data-search-close]');
    if (closeBtn) { closeBtn.classList.remove('opacity-100'); closeBtn.classList.add('opacity-0'); }
    if (searchPanel) { searchPanel.classList.add('-translate-y-full'); searchPanel.classList.remove('translate-y-0'); }
    body.classList.remove('drawer-open');
  };
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-search-open]')) openSearch();
    if (e.target.closest('[data-search-close]')) closeSearch();
    const tag = e.target.closest('[data-search-tag]');
    if (tag) qsa('[data-search-input]').forEach(i => i.value = tag.getAttribute('data-search-tag'));
  });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeSearch(); });

  /* ══════════════════════════════════════
     CART — OPTIMISTIC UI + DEBOUNCE ⚡
     ══════════════════════════════════════ */

  /* Local cart state — avoids refetching on every interaction */
  let cartState = null;
  let upsellHtml = null; // Cache upsell — don't refetch every time

  const fetchCart = async () => {
    const cart = await (await fetch('/cart.js')).json();
    cartState = cart;
    return cart;
  };

  /* Delivery estimate */
  const getDeliveryEstimate = () => {
    const addBizDays = (start, days) => {
      const d = new Date(start); let added = 0;
      while (added < days) { d.setDate(d.getDate() + 1); if (d.getDay() !== 0 && d.getDay() !== 6) added++; }
      return d;
    };
    const fmt = (d) => `${'Sun Mon Tue Wed Thu Fri Sat'.split(' ')[d.getDay()]} ${d.getDate()}.${d.getMonth()+1}`;
    const now = new Date();
    return `Delivery: approx. ${fmt(addBizDays(now, 3))} - ${fmt(addBizDays(now, 5))}`;
  };

  /* Render a single cart item (used for both initial and optimistic renders) */
  const renderCartItemHTML = (item, isLast) => {
    const imgSrc = item.featured_image?.url || item.image || '';
    const variantTitle = item.variant_title && item.variant_title !== 'Default Title'
      ? `<p class="text-xs text-gray-500 font-['Montserrat'] mb-1">${item.variant_title}</p>` : '';
    const unitPrice = item.final_line_price / item.quantity;

    return `
      <div class="cart-line-item ${!isLast ? 'border-b border-gray-200 mb-4 pb-4' : ''}" data-line-key="${item.key}" data-unit-price="${unitPrice}" style="transition:opacity .2s,max-height .3s;max-height:200px;opacity:1;">
        <div class="flex gap-4">
          <div class="w-20 h-20 flex-shrink-0 bg-gray-100">
            ${imgSrc ? `<img src="${imgSrc}" alt="${item.product_title}" class="w-full h-full object-cover" loading="lazy">` : ''}
          </div>
          <div class="flex-1 min-w-0">
            <h3 class="font-bold font-['Montserrat'] text-[#1a1a1a] text-sm mb-1">${item.product_title}</h3>
            ${variantTitle}
            <p class="font-bold text-[#1a1a1a] font-['Montserrat'] text-sm mb-2 cart-line-price">${formatMoney(item.final_line_price)}</p>
            <div class="flex items-center gap-3">
              <div class="flex items-center border border-gray-300" data-cart-item data-key="${item.key}">
                <button class="p-2 hover:bg-gray-100 transition-colors" data-qty-decrease>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="5" y1="12" x2="19" y2="12"/></svg>
                </button>
                <span class="px-3 text-sm font-['Montserrat'] font-bold cart-qty-display">${item.quantity}</span>
                <button class="p-2 hover:bg-gray-100 transition-colors" data-qty-increase>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                </button>
              </div>
              <button class="text-xs text-gray-500 hover:text-[#7e0505] font-['Montserrat'] underline transition-colors" data-cart-item data-key="${item.key}" data-remove-item>Remove</button>
            </div>
          </div>
        </div>
      </div>`;
  };

  const renderEmptyCart = () => `
    <div class="flex flex-col items-center justify-center h-full text-center">
      <p class="font-['Montserrat'] text-[#666666] text-base mb-4">Your cart is empty</p>
      <a href="/collections" class="bg-[#1a1a1a] text-white font-['Montserrat'] font-bold text-sm uppercase px-8 py-3 hover:bg-[#333] transition-colors">Start Shopping</a>
    </div>`;

  /* Full cart render (used for initial load and after server sync) */
  const renderFullCart = (cart) => {
    const container = qs('[data-cart-items]');
    if (!container) return;
    if (cart.item_count === 0) {
      container.innerHTML = renderEmptyCart();
      const ud = qs('[data-cart-upsell-desktop]');
      const um = qs('[data-cart-upsell-mobile]');
      if (ud) ud.innerHTML = '';
      if (um) um.innerHTML = '';
      return;
    }
    container.innerHTML = cart.items.map((item, i) => renderCartItemHTML(item, i === cart.items.length - 1)).join('');
  };

  /* Update UI elements that show cart totals/shipping */
  const updateCartChrome = (itemCount, totalPrice) => {
    qsa('[data-cart-count]').forEach(el => {
      el.textContent = itemCount;
      el.classList.toggle('hidden', itemCount === 0);
    });
    qsa('[data-cart-subtotal]').forEach(el => el.textContent = formatMoney(totalPrice));

    const fill = qs('[data-shipping-fill]');
    const text = qs('[data-shipping-text]');
    if (fill) {
      const pct = Math.min(totalPrice / 50000, 1) * 100;
      fill.style.width = pct + '%';
      if (totalPrice >= 50000) {
        fill.classList.remove('bg-[#f5afcb]'); fill.classList.add('bg-[#2d8a4e]');
        if (text) { text.textContent = "✓ You've unlocked free shipping!"; text.classList.add('text-[#2d8a4e]'); }
      } else {
        fill.classList.add('bg-[#f5afcb]'); fill.classList.remove('bg-[#2d8a4e]');
        if (text) { text.textContent = `You're R${((50000 - totalPrice) / 100).toFixed(2)} away from free shipping!`; text.classList.remove('text-[#2d8a4e]'); }
      }
    }
    const shippingPrice = qs('[data-shipping-price]');
    if (shippingPrice) shippingPrice.innerHTML = totalPrice >= 50000 ? '<span class="text-[#2d8a4e]">Free</span>' : 'Calculated at checkout';
    const deliveryEl = qs('[data-delivery-estimate]');
    if (deliveryEl) deliveryEl.textContent = itemCount > 0 ? getDeliveryEstimate() : '';
  };

  /* ── OPTIMISTIC quantity change ── */
  const pendingUpdates = new Map(); // key → newQty

  const flushCartUpdates = debounce(async () => {
    const updates = {};
    pendingUpdates.forEach((qty, key) => { updates[key] = qty; });
    pendingUpdates.clear();

    try {
      const cart = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      }).then(r => r.json());

      cartState = cart;
      /* Silent correction — only if server state differs from what we show */
      syncCartWithServer(cart);
    } catch {
      /* On error, do a hard refresh */
      const cart = await fetchCart();
      renderFullCart(cart);
      updateCartChrome(cart.item_count, cart.total_price);
    }
  }, 350);

  /* After server responds, check if our optimistic state matches */
  const syncCartWithServer = (cart) => {
    let needsRebuild = false;

    /* Check each line item */
    const serverKeys = new Set(cart.items.map(i => i.key));
    const domItems = qsa('.cart-line-item');

    domItems.forEach(el => {
      const key = el.dataset.lineKey;
      if (!serverKeys.has(key)) { needsRebuild = true; return; }
      const serverItem = cart.items.find(i => i.key === key);
      const displayQty = parseInt(el.querySelector('.cart-qty-display')?.textContent || '0');
      if (serverItem.quantity !== displayQty) needsRebuild = true;
    });

    if (cart.items.length !== domItems.length) needsRebuild = true;

    if (needsRebuild) renderFullCart(cart);
    updateCartChrome(cart.item_count, cart.total_price);
  };

  /* Optimistic qty update — instant visual, debounced API */
  const optimisticQtyChange = (key, delta) => {
    const lineEl = qs(`[data-line-key="${key}"]`);
    if (!lineEl) return;

    const qtyDisplay = lineEl.querySelector('.cart-qty-display');
    const priceDisplay = lineEl.querySelector('.cart-line-price');
    const unitPrice = parseInt(lineEl.dataset.unitPrice) || 0;

    let currentQty = parseInt(qtyDisplay.textContent);
    let newQty = Math.max(1, currentQty + delta);

    /* ⚡ INSTANT visual update */
    qtyDisplay.textContent = newQty;
    if (priceDisplay) priceDisplay.textContent = formatMoney(unitPrice * newQty);

    /* Recalculate totals optimistically */
    const qtyDiff = newQty - currentQty;
    if (cartState) {
      const item = cartState.items.find(i => i.key === key);
      if (item) {
        item.quantity = newQty;
        item.final_line_price = unitPrice * newQty;
        cartState.item_count += qtyDiff;
        cartState.total_price += qtyDiff * unitPrice;
        updateCartChrome(cartState.item_count, cartState.total_price);
      }
    }

    /* Queue for debounced API call */
    pendingUpdates.set(key, newQty);
    flushCartUpdates();
  };

  /* Optimistic remove — instant fade out + collapse */
  const optimisticRemove = (key) => {
    const lineEl = qs(`[data-line-key="${key}"]`);
    if (lineEl) {
      const unitPrice = parseInt(lineEl.dataset.unitPrice) || 0;
      const qty = parseInt(lineEl.querySelector('.cart-qty-display')?.textContent || '1');

      /* ⚡ Instant fade + collapse */
      lineEl.style.opacity = '0';
      lineEl.style.maxHeight = '0';
      lineEl.style.marginBottom = '0';
      lineEl.style.paddingBottom = '0';
      lineEl.style.overflow = 'hidden';
      setTimeout(() => lineEl.remove(), 300);

      /* Update local state */
      if (cartState) {
        cartState.items = cartState.items.filter(i => i.key !== key);
        cartState.item_count -= qty;
        cartState.total_price -= unitPrice * qty;
        updateCartChrome(cartState.item_count, cartState.total_price);

        if (cartState.item_count <= 0) {
          setTimeout(() => {
            const container = qs('[data-cart-items]');
            if (container) container.innerHTML = renderEmptyCart();
          }, 320);
        }
      }
    }

    /* Immediate API call for removes (no debounce — user expects it gone) */
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 })
    }).then(r => r.json()).then(cart => {
      cartState = cart;
      syncCartWithServer(cart);
    }).catch(() => {});
  };

  /* Cart item click handler */
  document.addEventListener('click', (e) => {
    /* Remove button */
    const removeBtn = e.target.closest('[data-remove-item]');
    if (removeBtn) {
      const key = removeBtn.dataset.key;
      if (key) optimisticRemove(key);
      return;
    }

    /* Qty buttons */
    const item = e.target.closest('[data-cart-item]');
    if (!item) return;
    const key = item.dataset.key;
    if (!key) return;

    if (e.target.closest('[data-qty-increase]')) optimisticQtyChange(key, 1);
    if (e.target.closest('[data-qty-decrease]')) optimisticQtyChange(key, -1);
  });

  /* Full cart UI update (used when opening drawer or after add-to-cart) */
  const updateCartUI = async () => {
    try {
      const cart = await fetchCart();
      updateCartChrome(cart.item_count, cart.total_price);
      renderFullCart(cart);
      loadUpsell(cart);
    } catch {}
  };

  /* Cart open — fetch fresh + render */
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-cart-open]')) {
      openCart();
      updateCartUI();
    }
  });

  /* ── Upsell (cached — only refetch on add-to-cart, not qty changes) ── */
  const loadUpsell = async (cart) => {
    const desktopSlot = qs('[data-cart-upsell-desktop]');
    const mobileSlot = qs('[data-cart-upsell-mobile]');
    if (!desktopSlot && !mobileSlot) return;
    if (!cart.items.length) {
      if (desktopSlot) desktopSlot.innerHTML = '';
      if (mobileSlot) mobileSlot.innerHTML = '';
      upsellHtml = null;
      return;
    }

    /* Use cached upsell if available */
    if (upsellHtml) {
      if (desktopSlot) desktopSlot.innerHTML = upsellHtml;
      if (mobileSlot) mobileSlot.innerHTML = upsellHtml;
      return;
    }

    const cartProductIds = cart.items.map(i => i.product_id);
    try {
      let upsellProduct = null;
      const res = await fetch(`/recommendations/products.json?product_id=${cartProductIds[0]}&limit=4`);
      const data = await res.json();
      if (data.products?.length) upsellProduct = data.products.find(p => !cartProductIds.includes(p.id));
      if (!upsellProduct) {
        const colRes = await fetch('/collections/all/products.json?limit=8');
        const colData = await colRes.json();
        if (colData.products) upsellProduct = colData.products.find(p => !cartProductIds.includes(p.id));
      }
      if (upsellProduct) {
        const imgSrc = upsellProduct.featured_image || upsellProduct.images?.[0]?.src || '';
        upsellHtml = `
          <div class="bg-[#1a1a1a] px-6 py-4">
            <div class="flex items-center justify-between gap-4">
              <div class="flex-1">
                <h3 class="text-white font-bold font-['Montserrat'] text-base mb-1">Great Choice!</h3>
                <p class="text-white/80 text-sm font-['Montserrat']">What else do you need?</p>
              </div>
              <a href="${upsellProduct.url || `/products/${upsellProduct.handle}`}" class="w-20 h-20 flex-shrink-0 block">
                ${imgSrc ? `<img src="${imgSrc}" alt="${upsellProduct.title}" class="w-full h-full object-cover" loading="lazy">` : ''}
              </a>
            </div>
          </div>`;
        if (desktopSlot) desktopSlot.innerHTML = upsellHtml;
        if (mobileSlot) mobileSlot.innerHTML = upsellHtml;
      }
    } catch {}
  };

  /* ══════════════════════════════════════
     ADD TO CART — All forms, AJAX ⚡
     ══════════════════════════════════════ */
  document.addEventListener('submit', (e) => {
    /* Handle both data-product-form AND data-ajax-add forms */
    const form = e.target.closest('[data-product-form]') || e.target.closest('[data-ajax-add]') || (e.target.matches('[data-product-form], [data-ajax-add]') ? e.target : null);
    if (!form) return;
    e.preventDefault();

    const btn = form.querySelector('[type=submit], button[type=submit], button:not([type])');
    const originalText = btn?.dataset.originalText || btn?.textContent?.trim() || 'Add to Cart';
    if (btn) { btn.disabled = true; btn.textContent = 'Adding...'; }

    /* ⚡ Optimistic badge update */
    const qtyInput = form.querySelector('[data-qty-input], input[name="quantity"]');
    const addQty = parseInt(qtyInput?.value || '1');
    qsa('[data-cart-count]').forEach(el => {
      const current = parseInt(el.textContent) || 0;
      el.textContent = current + addQty;
      el.classList.remove('hidden');
    });

    /* Invalidate upsell cache on new product */
    upsellHtml = null;

    fetch('/cart/add.js', { method: 'POST', body: new FormData(form) })
      .then(r => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(() => {
        if (btn) {
          btn.textContent = '✓ Added!';
          setTimeout(() => { btn.disabled = false; btn.textContent = originalText; }, 2000);
        }
        updateCartUI();
        openCart();
      })
      .catch(() => {
        if (btn) { btn.disabled = false; btn.textContent = originalText; }
        /* Revert optimistic badge */
        qsa('[data-cart-count]').forEach(el => {
          const current = parseInt(el.textContent) || 0;
          el.textContent = Math.max(0, current - addQty);
        });
      });
  });

  /* Save original button text for all add-to-cart forms */
  qsa('[data-product-form] [type=submit], [data-product-form] button, [data-ajax-add] [type=submit], [data-ajax-add] button').forEach(btn => {
    if (!btn.dataset.originalText) btn.dataset.originalText = btn.textContent.trim();
  });

  /* ══════════════════════════════════════
     COLLECTION — Filter & Sort
     ══════════════════════════════════════ */
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-filter-toggle]')) {
      const drawer = qs('[data-filter-drawer]');
      if (drawer) drawer.style.display = drawer.style.display === 'none' ? 'block' : 'none';
    }
  });

  const sortSelect = qs('[data-sort-select]');
  if (sortSelect) {
    sortSelect.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortSelect.value);
      window.location.href = url.toString();
    });
  }

  /* ══════════════════════════════════════
     ANNOUNCEMENT BAR — Smooth crossfade
     ══════════════════════════════════════ */
  const announcement = qs('[data-announcement]');
  if (announcement) {
    const msgs = qsa('[data-announcement-message]', announcement);
    if (msgs.length > 1) {
      let idx = 0;
      setInterval(() => {
        msgs[idx].classList.remove('opacity-100'); msgs[idx].classList.add('opacity-0');
        idx = (idx + 1) % msgs.length;
        msgs[idx].classList.remove('opacity-0'); msgs[idx].classList.add('opacity-100');
      }, 4000);
    }
  }

  /* ══════════════════════════════════════
     PRODUCT CAROUSEL — Smooth scroll
     ══════════════════════════════════════ */
  qsa('[data-carousel]').forEach((track) => {
    const prev = track.parentElement.querySelector('[data-carousel-prev]');
    const next = track.parentElement.querySelector('[data-carousel-next]');
    if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -300, behavior: 'smooth' }));
    if (next) next.addEventListener('click', () => track.scrollBy({ left: 300, behavior: 'smooth' }));
  });

  /* ══════════════════════════════════════
     PREFETCH — Hover-based link prefetching ⚡
     ══════════════════════════════════════ */
  const prefetched = new Set();
  document.addEventListener('pointerenter', (e) => {
    const link = e.target.closest('a[href]');
    if (!link) return;
    const href = link.href;
    if (!href || prefetched.has(href) || href.includes('#') || link.target === '_blank') return;
    if (!href.startsWith(window.location.origin)) return;
    prefetched.add(href);
    const prefetchLink = document.createElement('link');
    prefetchLink.rel = 'prefetch';
    prefetchLink.href = href;
    document.head.appendChild(prefetchLink);
  }, { passive: true, capture: true });

})();
